import { debugLog } from "../utils/debug";
import { API_BASE_URL } from "../config/environment";

/**
 * User Lookup Service
 * Handles looking up existing user data from Google Sheets
 */
class UserLookupService {
  constructor() {
    this.baseUrl = API_BASE_URL;
  }

  /**
   * Look up user by figure skating membership number
   * @param {string} membershipNumber - The figure skating membership number
   * @returns {Promise<Object>} User data from Google Sheets
   */
  async lookupByMembershipNumber(membershipNumber) {
    try {
      debugLog("Looking up user by membership number:", membershipNumber);

      const response = await fetch(`${this.baseUrl}/users/lookup`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ membershipNumber }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.error || `HTTP ${response.status}: ${response.statusText}`
        );
      }

      const data = await response.json();
      debugLog("User lookup successful:", data);

      return data;
    } catch (error) {
      debugLog("User lookup failed:", error);
      throw error;
    }
  }

  /**
   * Look up user by email address
   * @param {string} email - The user's email address
   * @returns {Promise<Object>} User data from Google Sheets
   */
  async lookupByEmail(email) {
    try {
      debugLog("Looking up user by email:", email);

      const response = await fetch(`${this.baseUrl}/users/lookup`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.error || `HTTP ${response.status}: ${response.statusText}`
        );
      }

      const data = await response.json();
      debugLog("User lookup successful:", data);

      return data;
    } catch (error) {
      debugLog("User lookup failed:", error);
      throw error;
    }
  }

  /**
   * Search for users by name (partial match)
   * @param {string} searchTerm - Name to search for
   * @returns {Promise<Object>} Search results from Google Sheets
   */
  async searchByName(searchTerm) {
    try {
      debugLog("Searching users by name:", searchTerm);

      const response = await fetch(`${this.baseUrl}/users/search`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ searchTerm }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.error || `HTTP ${response.status}: ${response.statusText}`
        );
      }

      const data = await response.json();
      debugLog("User search successful:", data);

      return data;
    } catch (error) {
      debugLog("User search failed:", error);
      throw error;
    }
  }
}

const userLookupService = new UserLookupService();

export default userLookupService;
